import {
  Button,
  ButtonBase,
  ButtonProps,
  Divider,
  Popover,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useCompanyId } from '@cliengo/mfe-utils/hooks';
import i18n from 'i18next';

import { Flex, Show } from '../utils';

import { useNavigationContext } from './Navigation';
import { WhatsappIcon } from './Icons';

import { StyledTooltip } from '@/ui/Tooltip/Tooltip';
import * as track from '@/utils/eventTracker';
import { useTranslate } from '@/hooks';

export const SupportButton = () => {
  const { isSmall } = useNavigationContext();
  const companyId = useCompanyId();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslate('profileButtom');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    track.clickProfile();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'support-popover' : undefined;

  const handleOpenWhatsapp = () => {
    track.goToHelpWa();
    //Whatsapp de Lia
    const whatsappSupportNumber = '5491134297150';

    const message = encodeURIComponent(
      i18n.t('supports.companyInquiry', { companyId })
    );
    // eslint-disable-next-line max-len
    const url = `https://api.whatsapp.com/send/?phone=${whatsappSupportNumber}&text=${message}`;

    window.open(url, '_blank');
  };

  const handleGoToSupport = () => {
    track.goToHelpWeb();
    navigate('/support');
  };

  useEffect(() => {
    handleClose();
  }, [location]);

  return (
    <>
      <Show when={isSmall}>
        <ButtonBase
          onClick={handleClick}
          sx={{
            color: '#21005D',
            height: '46px',
            width: '46px',
            borderRadius: '999px',
          }}
        >
          <Icon />
        </ButtonBase>
      </Show>
      <Show when={!isSmall}>
        <StyledTooltip title={t('supports.support')}>
          <ButtonBase
            onClick={handleClick}
            sx={{
              color: '#21005D',
              height: '46px',
              width: '46px',
              borderRadius: '999px',
            }}
          >
            <Icon />
          </ButtonBase>
        </StyledTooltip>
      </Show>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Flex
          gap=".33rem"
          column
          sx={{
            alignItems: 'center',
          }}
        >
          <Item onClick={handleGoToSupport} startIcon={<Icon />}>
            {t('supports.webSupport')}
          </Item>
          <Divider />
          <Item onClick={handleOpenWhatsapp} startIcon={<WhatsappIcon />}>
            {t('supports.whatsappSupport')}
          </Item>
        </Flex>
      </Popover>
    </>
  );
};

const Icon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M4.88464 9.50073V6.46611C4.89366 5.86961 5.02028 5.28075 5.25726 4.73327C5.49425 4.18579 5.83693 3.69045 6.26568 3.27564C6.69443 2.86082 7.20081 2.53469 7.75582 2.31591C8.31083 2.09714 8.90355 1.99003 9.50003 2.00073C10.0965 1.99003 10.6892 2.09714 11.2442 2.31591C11.7992 2.53469 12.3056 2.86082 12.7344 3.27564C13.1631 3.69045 13.5058 4.18579 13.7428 4.73327C13.9798 5.28075 14.1064 5.86961 14.1154 6.46611V9.50073"
        stroke="#21005D"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.15379 7.77002H4.30763C4.46064 7.77002 4.60738 7.8308 4.71558 7.939C4.82377 8.04719 4.88455 8.19393 4.88455 8.34694V11.8085C4.88455 11.9615 4.82377 12.1082 4.71558 12.2164C4.60738 12.3246 4.46064 12.3854 4.30763 12.3854H3.15379C2.84777 12.3854 2.55428 12.2638 2.33789 12.0475C2.1215 11.8311 1.99994 11.5376 1.99994 11.2316V8.92387C1.99994 8.61785 2.1215 8.32436 2.33789 8.10797C2.55428 7.89159 2.84777 7.77002 3.15379 7.77002Z"
        fill="#21005D"
        fillOpacity="0.101961"
      />
      <path
        d="M15.8461 12.3854H14.6922C14.5392 12.3854 14.3925 12.3246 14.2843 12.2164C14.1761 12.1082 14.1153 11.9615 14.1153 11.8085V8.34694C14.1153 8.19393 14.1761 8.04719 14.2843 7.939C14.3925 7.8308 14.5392 7.77002 14.6922 7.77002H15.8461C16.1521 7.77002 16.4456 7.89159 16.662 8.10797C16.8784 8.32436 16.9999 8.61785 16.9999 8.92387V11.2316C16.9999 11.5376 16.8784 11.8311 16.662 12.0475C16.4456 12.2638 16.1521 12.3854 15.8461 12.3854Z"
        fill="#21005D"
        fillOpacity="0.101961"
      />
      <path
        d="M11.8076 15.5584C12.4197 15.5584 13.0066 15.3153 13.4394 14.8825C13.8722 14.4497 14.1153 13.8627 14.1153 13.2507V10.6545"
        stroke="#21005D"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.8076 15.5583C11.8076 15.9408 11.6556 16.3077 11.3851 16.5781C11.1146 16.8486 10.7478 17.0006 10.3652 17.0006H8.63448C8.25195 17.0006 7.8851 16.8486 7.61461 16.5781C7.34413 16.3077 7.19217 15.9408 7.19217 15.5583C7.19217 15.1758 7.34413 14.8089 7.61461 14.5384C7.8851 14.2679 8.25195 14.116 8.63448 14.116H10.3652C10.7478 14.116 11.1146 14.2679 11.3851 14.5384C11.6556 14.8089 11.8076 15.1758 11.8076 15.5583Z"
        fill="#21005D"
        fillOpacity="0.101961"
      />
      <path
        d="M3.15379 7.77002H4.30763C4.46064 7.77002 4.60738 7.8308 4.71558 7.939C4.82377 8.04719 4.88455 8.19393 4.88455 8.34694V11.8085C4.88455 11.9615 4.82377 12.1082 4.71558 12.2164C4.60738 12.3246 4.46064 12.3854 4.30763 12.3854H3.15379C2.84777 12.3854 2.55428 12.2638 2.33789 12.0475C2.1215 11.8311 1.99994 11.5376 1.99994 11.2316V8.92387C1.99994 8.61785 2.1215 8.32436 2.33789 8.10797C2.55428 7.89159 2.84777 7.77002 3.15379 7.77002Z"
        stroke="#21005D"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.8461 12.3854H14.6922C14.5392 12.3854 14.3925 12.3246 14.2843 12.2164C14.1761 12.1082 14.1153 11.9615 14.1153 11.8085V8.34694C14.1153 8.19393 14.1761 8.04719 14.2843 7.939C14.3925 7.8308 14.5392 7.77002 14.6922 7.77002H15.8461C16.1521 7.77002 16.4456 7.89159 16.662 8.10797C16.8784 8.32436 16.9999 8.61785 16.9999 8.92387V11.2316C16.9999 11.5376 16.8784 11.8311 16.662 12.0475C16.4456 12.2638 16.1521 12.3854 15.8461 12.3854Z"
        stroke="#21005D"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.8076 15.5583C11.8076 15.9408 11.6556 16.3077 11.3851 16.5781C11.1146 16.8486 10.7478 17.0006 10.3652 17.0006H8.63448C8.25195 17.0006 7.8851 16.8486 7.61461 16.5781C7.34413 16.3077 7.19217 15.9408 7.19217 15.5583C7.19217 15.1758 7.34413 14.8089 7.61461 14.5384C7.8851 14.2679 8.25195 14.116 8.63448 14.116H10.3652C10.7478 14.116 11.1146 14.2679 11.3851 14.5384C11.6556 14.8089 11.8076 15.1758 11.8076 15.5583Z"
        stroke="#21005D"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const Item = (props: ButtonProps) => {
  const { children, sx, ...other } = props;

  return (
    <Button
      fullWidth
      variant="text"
      sx={{
        color: '#424242',
        justifyContent: 'flex-start',
        px: 1,
        py: 1,
        m: 0,
        fontWeight: 400,
        fontSize: '14px',
        ...sx,
      }}
      {...other}
    >
      {children}
    </Button>
  );
};
